import React, { useState } from 'react';
import {
    makeStyles,
    Grid,
    Paper,
    Typography,
    TextField,
    MenuItem,
    Divider,
    Badge,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    IconButton
} from '@material-ui/core';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
    sidebar: {
        padding: theme.spacing(3),
        borderRight: '1px solid #e0e0e0',
        height: '100%',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 200px)' // Permite scroll
    },
    calendarContainer: {
        padding: theme.spacing(3)
    },
    slotCard: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        cursor: 'pointer',
        borderRadius: '8px',
        transition: 'all 0.2s',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }
    },
    slotFree: {
        backgroundColor: '#e8f5e9',
        borderLeft: '4px solid #4caf50'
    },
    slotBusy: {
        backgroundColor: '#e3f2fd',
        borderLeft: '4px solid #2196f3'
    },
    slotLunch: {
        backgroundColor: '#f5f5f5',
        borderLeft: '4px solid #9e9e9e',
        cursor: 'not-allowed',
        opacity: 0.7
    },
    slotPast: {
        backgroundColor: '#fafafa',
        borderLeft: '4px solid #bdbdbd',
        cursor: 'not-allowed',
        opacity: 0.6
    },
    slotTime: {
        fontWeight: 'bold',
        fontSize: '1rem',
        marginBottom: theme.spacing(0.5)
    },
    slotStatus: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary
    },
    calendarGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2)
    },
    calendarDay: {
        padding: theme.spacing(1),
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        minHeight: '60px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#f5f5f5'
        }
    },
    calendarDaySelected: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark
        }
    },
    calendarDayToday: {
        border: `2px solid ${theme.palette.primary.main}`
    },
    calendarDayOtherMonth: {
        opacity: 0.3
    },
    calendarDayNumber: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(0.5)
    },
    appointmentDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        marginTop: theme.spacing(0.5)
    },
    weekDayHeader: {
        padding: theme.spacing(1),
        textAlign: 'center',
        fontWeight: 'bold',
        color: theme.palette.text.secondary
    },
    slotsContainer: {
        maxHeight: '400px',
        overflowY: 'auto',
        marginTop: theme.spacing(2),
        paddingRight: theme.spacing(1)
    }
}));

const CalendarView = ({
    date,
    onDateSelect,
    appointments,
    slots,
    onSlotClick,
    professionals,
    selectedProfessionalId,
    setSelectedProfessionalId,
    subSpecialties,
    selectedSubSpecialtyId,
    setSelectedSubSpecialtyId,
    onConfirm,
    onCancel
}) => {
    const classes = useStyles();

    // Funções auxiliares
    const getStatusColor = (status) => {
        switch (status) {
            case "CONFIRMED":
                return "primary";
            case "CANCELED":
                return "secondary";
            default:
                return "default";
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case "CONFIRMED":
                return "Confirmado";
            case "CANCELED":
                return "Cancelado";
            default:
                return "Pendente";
        }
    };

    // Gerar dias do mês
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Preencher dias antes do início do mês
    const startDayOfWeek = monthStart.getDay();
    const previousMonthDays = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const day = new Date(monthStart);
        day.setDate(day.getDate() - (i + 1));
        previousMonthDays.push(day);
    }

    // Preencher dias depois do fim do mês
    const endDayOfWeek = monthEnd.getDay();
    const nextMonthDays = [];
    for (let i = 1; i < 7 - endDayOfWeek; i++) {
        const day = new Date(monthEnd);
        day.setDate(day.getDate() + i);
        nextMonthDays.push(day);
    }

    const allDays = [...previousMonthDays, ...daysInMonth, ...nextMonthDays];

    // Verificar se um dia tem agendamentos
    const hasAppointments = (day) => {
        return appointments.some(appt => {
            const apptDate = new Date(appt.date);
            return isSameDay(apptDate, day);
        });
    };

    // Handler para clique em dia
    const handleDayClick = (day) => {
        if (isSameMonth(day, date)) {
            onDateSelect(day);
        }
    };

    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    return (
        <Paper elevation={2}>
            {/* Filtros no Topo */}
            <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Profissional"
                            value={selectedProfessionalId}
                            onChange={(e) => setSelectedProfessionalId(e.target.value)}
                            variant="outlined"
                            size="small"
                        >
                            {professionals.map((prof) => (
                                <MenuItem key={prof.id} value={prof.id}>
                                    {prof.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Serviço"
                            value={selectedSubSpecialtyId}
                            onChange={(e) => setSelectedSubSpecialtyId(e.target.value)}
                            variant="outlined"
                            size="small"
                        >
                            <MenuItem value="">Todos os Serviços</MenuItem>
                            {subSpecialties.map((sub) => (
                                <MenuItem key={sub.id} value={sub.id}>
                                    {sub.name} ({sub.duration} min)
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </div>

            <Grid container>
                {/* Sidebar Esquerda - Apenas Horários */}
                <Grid item xs={12} md={3}>
                    <div className={classes.sidebar}>
                        {/* Horários do Dia Selecionado */}
                        <Typography variant="h6" gutterBottom>
                            Horários - {format(date, 'dd/MM/yyyy', { locale: ptBR })}
                        </Typography>

                        {slots.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                                Nenhum horário disponível para esta data.
                            </Typography>
                        ) : (
                            <div className={classes.slotsContainer}>
                                {slots.map((slot, index) => (
                                    <Paper
                                        key={index}
                                        className={`${classes.slotCard} ${slot.lunch ? classes.slotLunch :
                                            slot.appointment ? classes.slotBusy :
                                                slot.past ? classes.slotPast :
                                                    classes.slotFree
                                            }`}
                                        elevation={1}
                                        onClick={() => {
                                            if (!slot.lunch && (!slot.past || slot.appointment)) {
                                                onSlotClick(slot);
                                            }
                                        }}
                                    >
                                        <Typography className={classes.slotTime}>
                                            {slot.time}
                                        </Typography>
                                        <Typography className={classes.slotStatus}>
                                            {slot.lunch ? '🍽️ Almoço' :
                                                slot.appointment ? `👤 ${slot.appointment.patientName || 'Ocupado'}` :
                                                    slot.past ? '⏰ Encerrado' :
                                                        '✅ Disponível'}
                                        </Typography>
                                        {slot.appointment && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: (slot.appointment.status || "PENDING") === "CONFIRMED" ? "green" : "red",
                                                    fontWeight: "bold",
                                                    fontSize: "0.65rem",
                                                    display: "block",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                {(slot.appointment.status || "PENDING") === "CONFIRMED" ? "Confirmado" : "Não Confirmado"}
                                            </Typography>
                                        )}
                                    </Paper>
                                ))}
                            </div>
                        )}
                    </div>
                </Grid>

                {/* Calendário Principal */}
                <Grid item xs={12} md={9}>
                    <div className={classes.calendarContainer}>
                        {/* Header com navegação de mês */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <IconButton
                                onClick={() => onDateSelect(subMonths(date, 1))}
                                size="small"
                                title="Mês anterior"
                            >
                                <ChevronLeftIcon />
                            </IconButton>

                            <Typography variant="h5" style={{ textTransform: 'capitalize' }}>
                                {format(date, 'MMMM yyyy', { locale: ptBR })}
                            </Typography>

                            <IconButton
                                onClick={() => onDateSelect(addMonths(date, 1))}
                                size="small"
                                title="Próximo mês"
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </div>

                        {/* Cabeçalho dos dias da semana */}
                        <div className={classes.calendarGrid}>
                            {weekDays.map((day) => (
                                <div key={day} className={classes.weekDayHeader}>
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Dias do mês */}
                        <div className={classes.calendarGrid}>
                            {allDays.map((day, index) => {
                                const isSelected = isSameDay(day, date);
                                const isTodayDay = isToday(day);
                                const isCurrentMonth = isSameMonth(day, monthStart);
                                const dayHasAppointments = hasAppointments(day);

                                return (
                                    <Paper
                                        key={index}
                                        className={`${classes.calendarDay} ${isSelected ? classes.calendarDaySelected : ''
                                            } ${isTodayDay ? classes.calendarDayToday : ''} ${!isCurrentMonth ? classes.calendarDayOtherMonth : ''
                                            }`}
                                        elevation={isSelected ? 3 : 1}
                                        onClick={() => handleDayClick(day)}
                                    >
                                        <Typography className={classes.calendarDayNumber}>
                                            {format(day, 'd')}
                                        </Typography>
                                        {dayHasAppointments && isCurrentMonth && (
                                            <div
                                                className={classes.appointmentDot}
                                                style={{ backgroundColor: '#2196f3' }}
                                            />
                                        )}
                                    </Paper>
                                );
                            })}
                        </div>
                    </div>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default CalendarView;
